import React, { useState } from 'react';
import type { Assignment, Technician } from '@shared/types';
import { Calendar, Clock, MapPin, Phone, User, FileText, CheckCircle, PlayCircle, XCircle } from 'lucide-react';
import { format } from 'date-fns';

interface TechnicianAssignmentViewProps {
    assignment: Assignment;
    technicians: Technician[];
    onUpdateRemarque: (assignmentId: string, remarque: string) => void;
    onStartWorksite: (assignmentId: string) => void;
    onFinishWorksite: (assignmentId: string) => void;
    onImagesUpdate?: (images: any[]) => void;
    isReadOnly?: boolean;
    userRole?: 'admin' | 'technician' | 'planner';
    onClose?: () => void;
}

export const TechnicianAssignmentView: React.FC<TechnicianAssignmentViewProps> = ({
    assignment,
    technicians,
    onUpdateRemarque,
    onStartWorksite,
    onFinishWorksite,
    isReadOnly,
    onClose,
}) => {
    const [remarque, setRemarque] = useState(assignment.remarque || '');
    const [isSaving, setIsSaving] = useState(false);

    // --- OPTIMISTIC UI STATE ---
    // Use local status immediately after user click to prevent lag
    const [localStatus, setLocalStatus] = useState(assignment.status || 'pending');
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

    // Sync local status if prop changes (e.g. initial load or external update)
    // but only if we are NOT currently updating it ourselves to avoid race conditions flickering
    React.useEffect(() => {
        if (!isUpdatingStatus) {
            setLocalStatus(assignment.status || 'pending');
        }
    }, [assignment.status, isUpdatingStatus]);


    // Find technicians assigned to this worksite
    const assignedTechs = technicians.filter(tech =>
        tech.assignments.some(a => a.id === assignment.id)
    );

    const handleRemarqueChange = async (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = e.target.value;
        setRemarque(newValue);

        // Auto-save with debounce
        setIsSaving(true);
        setTimeout(() => {
            onUpdateRemarque(assignment.id, newValue);
            setIsSaving(false);
        }, 500);
    };


    const handleStartOptimistic = async () => {
        setIsUpdatingStatus(true);
        setLocalStatus('in-progress'); // Immediate UI update

        try {
            await onStartWorksite(assignment.id);

            // Wait briefly to show the "In Progress" state, then return to list
            setTimeout(() => {
                if (onClose) {
                    onClose();
                }
                setIsUpdatingStatus(false);
            }, 1000); // 1 second delay for visual feedback

        } catch (error) {
            console.error("Error starting worksite", error);
            setLocalStatus(assignment.status || 'pending');
            setIsUpdatingStatus(false);
        }
    };

    const handleFinishOptimistic = async () => {
        // 1. Ask for confirmation
        if (!window.confirm("Confirmer la clôture de l'intervention ?\nCette action est définitive pour le technicien.")) {
            return;
        }

        setIsUpdatingStatus(true);
        setLocalStatus('completed-by-tech'); // Immediate UI update

        try {
            await onFinishWorksite(assignment.id);

            // 2. Wait to show success state, THEN close and reset
            setTimeout(() => {
                if (onClose) {
                    onClose();
                }
                setIsUpdatingStatus(false);
            }, 1500); // 1.5 second delay for visual feedback

        } catch (error) {
            // Revert on error
            console.error("Error finishing worksite", error);
            setLocalStatus(assignment.status || 'pending');
            setIsUpdatingStatus(false);
        }
    };


    const getStatusBadge = () => {
        const statusConfig = {
            pending: { label: 'Planifié', color: 'bg-green-500', icon: Clock },
            'in-progress': { label: 'En cours', color: 'bg-orange-500', icon: PlayCircle },
            'completed-by-tech': { label: 'Terminé', color: 'bg-red-500', icon: CheckCircle },
            closed: { label: 'Clôturé', color: 'bg-blue-500', icon: XCircle },
            todo: { label: 'À Faire', color: 'bg-green-500', icon: Clock },
            done: { label: 'Fait', color: 'bg-red-500', icon: CheckCircle },
            blocked: { label: 'Bloqué', color: 'bg-gray-500', icon: XCircle }
        };

        const config = statusConfig[localStatus as keyof typeof statusConfig] || statusConfig.pending;
        const Icon = config.icon;

        return (
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg ${config.color} text-white font-medium shadow-md transition-all duration-300`}>
                {isUpdatingStatus ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                    <Icon className="w-5 h-5" />
                )}
                {config.label}
            </div>
        );
    };

    return (
        <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg p-6 space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {/* Header with Status */}
            <div className="flex items-start justify-between">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">{assignment.clientName}</h2>
                    <p className="text-gray-600 mt-1">{assignment.task || 'Intervention'}</p>
                </div>
                {getStatusBadge()}
            </div>

            {/* Read-only Assignment Details */}
            <div className="grid md:grid-cols-2 gap-4">
                {/* Dates */}
                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                    <Calendar className="w-5 h-5 text-gray-600 mt-1" />
                    <div>
                        <div className="text-sm font-medium text-gray-700">Dates</div>
                        <div className="text-gray-900">
                            {format(new Date(assignment.startDate), 'dd/MM/yyyy')} - {format(new Date(assignment.endDate), 'dd/MM/yyyy')}
                        </div>
                        {assignment.startTime && assignment.endTime && (
                            <div className="text-sm text-gray-600 mt-1">
                                {assignment.startTime} - {assignment.endTime}
                            </div>
                        )}
                    </div>
                </div>

                {/* Location */}
                {assignment.address && (
                    <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                        <MapPin className="w-5 h-5 text-gray-600 mt-1" />
                        <div>
                            <div className="text-sm font-medium text-gray-700">Adresse</div>
                            <div className="text-gray-900">{assignment.address}</div>
                            {assignment.city && assignment.postalCode && (
                                <div className="text-sm text-gray-600">
                                    {assignment.postalCode} {assignment.city}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Contact */}
                {(assignment.contactName || assignment.contactPhone) && (
                    <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                        <Phone className="w-5 h-5 text-gray-600 mt-1" />
                        <div>
                            <div className="text-sm font-medium text-gray-700">Contact</div>
                            {assignment.contactName && <div className="text-gray-900">{assignment.contactName}</div>}
                            {assignment.contactPhone && (
                                <a href={`tel:${assignment.contactPhone}`} className="text-blue-600 hover:underline">
                                    {assignment.contactPhone}
                                </a>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Team Members */}
            {assignedTechs.length > 1 && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-3">
                        <User className="w-5 h-5 text-blue-600" />
                        <h3 className="font-semibold text-blue-900">Équipe sur ce chantier</h3>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {assignedTechs.map(tech => (
                            <div
                                key={tech.id}
                                className="px-3 py-1 rounded-lg text-white font-medium"
                                style={{ backgroundColor: tech.color }}
                            >
                                {tech.name}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Editable Remarque Field */}
            {!isReadOnly && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-3">
                        <FileText className="w-5 h-5 text-yellow-600" />
                        <h3 className="font-semibold text-yellow-900">Remarques</h3>
                        {isSaving && <span className="text-sm text-gray-600 italic">Enregistrement...</span>}
                    </div>
                    <textarea
                        value={remarque}
                        onChange={handleRemarqueChange}
                        placeholder="Ajoutez vos remarques ici..."
                        className="w-full px-4 py-3 border border-yellow-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 bg-white text-gray-900"
                        rows={4}
                    />
                </div>
            )}

            {isReadOnly && remarque && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="text-sm font-medium text-yellow-800 mb-2">Remarques</div>
                    <p className="text-gray-800 italic">{remarque}</p>
                </div>
            )}

            {/* Action Buttons */}
            {!isReadOnly && (
                <div className="flex gap-4 pt-4 border-t">
                    {localStatus === 'pending' && (
                        <button
                            onClick={handleStartOptimistic}
                            disabled={isUpdatingStatus}
                            className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold rounded-lg transition-colors shadow-lg active:transform active:scale-95"
                        >
                            {isUpdatingStatus ? (
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            ) : (
                                <PlayCircle className="w-5 h-5" />
                            )}
                            Démarrer le chantier
                        </button>
                    )}

                    {/* Support both in-progress spellings */}
                    {(localStatus === 'in-progress' || localStatus === 'in_progress') && (
                        <button
                            onClick={handleFinishOptimistic}
                            disabled={isUpdatingStatus}
                            className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-semibold rounded-lg transition-colors shadow-lg active:transform active:scale-95"
                        >
                            {isUpdatingStatus ? (
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            ) : (
                                <CheckCircle className="w-5 h-5" />
                            )}
                            Terminer le chantier
                        </button>
                    )}

                    {localStatus === 'completed-by-tech' && (
                        <div className="flex-1 p-4 bg-green-50 border border-green-200 rounded-lg text-center animate-pulse">
                            <div className="flex flex-col items-center gap-2">
                                <CheckCircle className="w-8 h-8 text-green-500" />
                                <p className="text-green-800 font-bold text-lg">
                                    Chantier Terminé !
                                </p>
                                <p className="text-sm text-green-600">
                                    En attente de validation finale
                                </p>
                            </div>
                        </div>
                    )}

                    {localStatus === 'closed' && assignment.closedAt && (
                        <div className="flex-1 p-4 bg-gray-100 border border-gray-300 rounded-lg text-center">
                            <p className="text-gray-600 font-medium">
                                Chantier clôturé le {format(new Date(assignment.closedAt), 'dd/MM/yyyy à HH:mm')}
                            </p>
                        </div>
                    )}
                </div>
            )}

            {/* Timestamps */}
            {(assignment.startedAt || assignment.completedAt || assignment.closedAt) && (
                <div className="pt-4 border-t space-y-2 text-sm text-gray-600">
                    {assignment.startedAt && (
                        <div>Démarré le : {format(new Date(assignment.startedAt), 'dd/MM/yyyy à HH:mm')}</div>
                    )}
                    {assignment.completedAt && (
                        <div>Terminé le : {format(new Date(assignment.completedAt), 'dd/MM/yyyy à HH:mm')}</div>
                    )}
                    {assignment.closedAt && (
                        <div>Clôturé le : {format(new Date(assignment.closedAt), 'dd/MM/yyyy à HH:mm')}</div>
                    )}
                </div>
            )}
        </div>
    );
};
